import { get } from 'lodash-es';
import type { SetRequired, ValueOf } from 'type-fest';

import { Codeowners } from '../../consts/codeowners';

import { captureException, NetworkError, UNHANDLED_ERROR } from '.';

export const httpErrors = {
  unauthenticated: 'unauthenticated',
  notFound: 'notFound',
  timeout: 'timeout',
  unhandled: 'unhandled',
} as const;

export type CommonHttpErrors = ValueOf<typeof httpErrors>;
export type ErrorWithResponse = SetRequired<NetworkError, 'response'>;
export const mapNetworkStatuses = (error: ErrorWithResponse, codeowners: Codeowners) => {
  switch (error.response?.status) {
    case 0:
      return httpErrors.timeout;
    case 401:
      return httpErrors.unauthenticated;
    case 404:
      return httpErrors.notFound;
    case 500:
      captureException(error, codeowners);
      return httpErrors.unhandled;
    case 502:
      return httpErrors.unhandled;
    case 504:
      return httpErrors.unhandled;
    default:
      return UNHANDLED_ERROR;
  }
};

const matchErrorResponse = <Keys extends string>(
  { response }: ErrorWithResponse,
  patterns: Record<
    Keys,
    {
      status?: number;
      path: string;
      value: string;
    }
  >
): Keys | undefined => {
  return Object.keys(patterns).find((key) => {
    const { status, value, path } = patterns[key as Keys];

    const isStatusMatch = status ? response.status === status : true;
    const isValueMatch = get(response.data, path) === value;
    return isStatusMatch && isValueMatch;
  }) as Keys | undefined;
};

export const errorResponseMatcher =
  <TNetworkErrors extends string>(
    patterns: Record<
      TNetworkErrors,
      {
        status?: number;
        path: string;
        value: string;
      }
    >
  ) =>
  (error: ErrorWithResponse, codeowners: Codeowners): TNetworkErrors | CommonHttpErrors | typeof UNHANDLED_ERROR => {
    const mappedErrors = mapNetworkStatuses(error, codeowners);

    if (mappedErrors !== UNHANDLED_ERROR) {
      return mappedErrors as TNetworkErrors;
    }

    const customError = matchErrorResponse(error, patterns);

    return customError ?? UNHANDLED_ERROR;
  };
